import {getTestProps} from '@benchling/js-test-utils';
import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import React, {useCallback, useMemo, useState} from 'react';
import {EdgeAddChange, EdgeRemoveChange} from 'reactflow';
import {v4 as uuidv4} from 'uuid';

import * as analytics from 'analytics/Analytics';
import isArchived from 'archive/isArchived';
import {assert} from 'Asserts';
import {ActionOrigin, WORKFLOWS_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {ProcessFlowchartNodeDetailsType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {readQuerySafe, writeFragmentsToCache, writeQuery} from 'graphql-client/ApolloWrappers';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {assertIsAccessible} from 'graphql-client/InaccessibleInfo';
import {NodeDetailsSidepanelContext} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/nodes/NodeDetailsSidepanelContext';
import {createTaskNode} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/nodes/nodeUtils';
import {AllProcessTaskSchemasForSchemaConfigQuery} from 'processes/modals/utils/__generated/AllProcessTaskSchemasForSchemaConfigQuery';
import {ALL_PROCESS_TASK_SCHEMAS_FOR_SCHEMA_CONFIG_QUERY} from 'processes/modals/utils/ProcessesAllSchemasQueryUtils';
import {isFlowchartType} from 'processes/processes-platform/FeatureOptions';
import CreateProcessTaskDefinitionEditor from 'processes/settings/CreateProcessTaskDefinitionEditor';
import {buildExecutionNodeTypeIdentifier} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/useFlowchartState/useFlowchartStateAnalytics';
import Button, {ButtonStyle} from 'ui-library/buttons/Button';
import useOnMount from 'ui-library/hooks/useOnMount';
import {useFlowchartSidepanelManagerContext} from 'ui-library/reactflow/sidepanels/sidepanel-manager/FlowchartSidepanelUnsavedChangesContext';
import {
  SidepanelChange,
  SidepanelType,
} from 'ui-library/reactflow/sidepanels/sidepanel-manager/useFlowchartSidepanelManager';
import {useFlowchartSidepanelTabUnsavedChanges} from 'ui-library/reactflow/sidepanels/sidepanel-manager/useFlowchartSidepanelTabUnsavedChanges';
import SearchInput from 'ui-library/SearchInput';
import APIUtil from 'util/APIUtil';

import {ProcessTaskDefinitionGraphQL} from '../../../../../../graphql-fragments/processes/__generated/ProcessTaskDefinitionGraphQL';
import {ProcessTaskSchemaGraphQL} from '../../../../../../graphql-fragments/processes/schemas/__generated/ProcessTaskSchemaGraphQL';
import {AllConfigurableTaskDefinitionsQuery} from '../__generated/AllConfigurableTaskDefinitionsQuery';
import {buildControlFlowEdge} from '../buildStaticDataFromFlowchartConfigGraphQL';
import {
  FlowchartEdge,
  ProcessFlowchartEdgeType,
  ProcessFlowchartNodeType,
} from '../processFlowchartConfigEditorConstants';
import {
  isPlaceholderForAddExistingTaskSchemaData,
  isPlaceholderForTaskSchemaCreateData,
  PlaceholderNode,
} from '../processFlowchartConfigEditorNodeTypes';
import {createStaticNodeDataFromTaskDefinitionVersion} from '../processFlowchartConfigEditorUtils';
import {ALL_CONFIGURABLE_TASK_DEFINITIONS_QUERY, ConfigurableTaskDefinitionGQL} from '../useAllSchemasById';

function insertSortedByName<T>(list: ReadonlyArray<T>, item: T, getName: (t: T) => string): Array<T> {
  const name = getName(item);
  const result = [...list];
  const insertIndex = result.findIndex((existing) => getName(existing).localeCompare(name) > 0);
  if (insertIndex === -1) {
    result.push(item);
  } else {
    result.splice(insertIndex, 0, item);
  }
  return result;
}

export function cacheCreatedTaskDefinition(newTaskDefinition: ProcessTaskDefinitionGraphQL): void {
  const taskDefinitionsResult = readQuerySafe<AllConfigurableTaskDefinitionsQuery>({
    query: ALL_CONFIGURABLE_TASK_DEFINITIONS_QUERY,
  });
  const taskSchemasResult = readQuerySafe<AllProcessTaskSchemasForSchemaConfigQuery>({
    query: ALL_PROCESS_TASK_SCHEMAS_FOR_SCHEMA_CONFIG_QUERY,
  });
  if (taskDefinitionsResult != null) {
    const taskDefinitionsWithUpdate = insertSortedByName(
      taskDefinitionsResult.processTaskDefinitionsSupportingPlatformFeature,
      newTaskDefinition,
      (def) => (def.latestVersion != null ? assertIsAccessible(def.latestVersion.processTaskSchema).name : '')
    );
    writeQuery(
      {query: ALL_CONFIGURABLE_TASK_DEFINITIONS_QUERY},
      {
        ...taskDefinitionsResult,
        processTaskDefinitionsSupportingPlatformFeature: taskDefinitionsWithUpdate,
        __typename: 'Query',
      }
    );
  }

  if (taskSchemasResult != null) {
    const newTaskSchema = assertIsAccessible(newTaskDefinition.latestVersion!.processTaskSchema);
    const taskSchemasWithUpdate = insertSortedByName(
      taskSchemasResult.processTaskSchemasSupportingPlatformFeature,
      newTaskSchema,
      (schema) => schema.name
    );
    writeQuery(
      {query: ALL_PROCESS_TASK_SCHEMAS_FOR_SCHEMA_CONFIG_QUERY},
      {
        ...taskSchemasResult,
        processTaskSchemasSupportingPlatformFeature: taskSchemasWithUpdate,
        __typename: 'Query',
      }
    );
  }

  writeFragmentsToCache(getFragmentWrapper('ProcessTaskDefinition'), [newTaskDefinition]);
}

export function AddExistingTaskDefinitionPane(props: {
  taskDefinitionsForSidepanel: Array<ConfigurableTaskDefinitionGQL>;
  createNodeFromTaskDefinition: (
    taskDefinition: ConfigurableTaskDefinitionGQL,
    shouldOpenSidepanel: boolean
  ) => Promise<void>;
  flowchartTypeName: string;
}): JSX.Element {
  const [searchQuery, setSearchQuery] = useState<string | null>(null);

  return (
    <div
      className={css(styles.wrapper)}
      {...getTestProps({component: 'AddExistingTaskDefinitionPane', element: 'root'})}
    >
      <SearchInput
        placeholder="Search"
        doSearch={(newSearchQuery) => {
          setSearchQuery(newSearchQuery);
          if (newSearchQuery != null && newSearchQuery.length > 0) {
            analytics.trackEvent(WORKFLOWS_ANALYTICS_ACTIONS.SEARCHED_TASK_SCHEMAS_IN_CANVAS, {
              actionOrigin: ActionOrigin.WORKFLOWS_FLOWCHART_FIRST_CONFIGURATION_CANVAS,
              searchSource: 'right_sidepanel',
              flowchartType: props.flowchartTypeName,
            });
          }
        }}
        query={searchQuery}
      />
      <div className={css(styles.addExistingTaskSchemaOptionsContainer)}>
        {props.taskDefinitionsForSidepanel
          .toSorted((left, right) =>
            left.latestVersion.processTaskSchema.name.localeCompare(
              right.latestVersion.processTaskSchema.name
            )
          )
          .filter(
            (taskDefinition) =>
              !isFlowchartType(taskDefinition.latestVersion.processTaskSchema.executionType) &&
              (searchQuery == null ||
                taskDefinition.latestVersion.processTaskSchema.name
                  .toLowerCase()
                  .includes(searchQuery.toLowerCase()))
          )
          .map((taskDefinition) => (
            <Button
              buttonStyle={ButtonStyle.UNBUTTON}
              key={taskDefinition.id}
              fullWidth={true}
              alignContentLeft={true}
              icon={ModelTypeConstants.PROCESS_TASK}
              textClassName={css(styles.addExistingTaskSchemaOptionText)}
              text={taskDefinition.latestVersion.processTaskSchema.name}
              className={css(styles.addExistingTaskSchemaOption)}
              onClick={() => {
                props.createNodeFromTaskDefinition(taskDefinition, false);
              }}
              dataTestAttrs={{
                component: 'AddExistingTaskDefinitionPane',
                element: 'TaskDefinitionOption',
                key: taskDefinition.id,
              }}
            />
          ))}
      </div>
    </div>
  );
}

export default function PlaceholderNodeDetailsSidepanelContent(props: {
  selectedNode: PlaceholderNode;
}): JSX.Element {
  const {
    flowchartTaskSchema,
    applyFlowchartChanges,
    interactiveFlowchartOptions,
    allSchemasById,
    interactiveFlowchart,
    taskDefinitionsForSidepanel,
    flowchartEditorConfigurator,
  } = NodeDetailsSidepanelContext.useContext();

  const sidepanelManager = useFlowchartSidepanelManagerContext();

  const onDiscard = useCallback(async () => {
    await applyFlowchartChanges((flowchartDraftState) => ({
      nodeChanges: [
        {
          type: 'remove',
          id: props.selectedNode.id,
        },
      ],
      edgeChanges: [],
    }));
  }, [applyFlowchartChanges, props.selectedNode.id]);

  const {setIsTabDirty} = useFlowchartSidepanelTabUnsavedChanges(
    `placeholder-${props.selectedNode.id}`,
    'Node Configuration',
    onDiscard
  );

  useOnMount(() => {
    setIsTabDirty(true);
  });

  const allTaskSchemas = useMemo(
    () =>
      Object.values(allSchemasById).filter(
        (schema): schema is ProcessTaskSchemaGraphQL =>
          schema != null && APIUtil.isProcessTaskSchema(schema.id)
      ),
    [allSchemasById]
  );

  const createNodeFromTaskDefinition = async (
    taskDefinition: ConfigurableTaskDefinitionGQL,
    shouldOpenSidepanel: boolean = true
  ): Promise<void> => {
    const newId = uuidv4();
    const taskSchema = assertIsAccessible(taskDefinition.latestVersion.processTaskSchema);
    const newAllTaskSchemas = [...allTaskSchemas];
    if (allTaskSchemas.find(({id}) => id === taskSchema.id) == null) {
      newAllTaskSchemas.push(taskSchema);
    }
    const staticNodeData = createStaticNodeDataFromTaskDefinitionVersion({
      nodeClientId: newId,
      taskDefinitionVersion: taskDefinition.latestVersion,
      nodeDetailsType: ProcessFlowchartNodeDetailsType.TASK_NODE_DETAILS,
      allTaskSchemas: newAllTaskSchemas,
    });
    const taskNode = createTaskNode(staticNodeData, props.selectedNode.position, interactiveFlowchartOptions);

    // We need to replace any edges that are connected to the placeholder node with edges to the new
    // node.
    const edgeAddChanges: Array<EdgeAddChange<FlowchartEdge>> = [];
    const edgeRemoveChanges: Array<EdgeRemoveChange> = [];
    for (const edge of interactiveFlowchart.edges) {
      if (edge.source !== props.selectedNode.id && edge.target !== props.selectedNode.id) {
        continue;
      }
      edgeRemoveChanges.push({
        type: 'remove',
        id: edge.id,
      });
      assert(
        edge.type !== ProcessFlowchartEdgeType.DATA_FLOW,
        'Data flow edges should not be connected to the placeholder node'
      );
      const newSource = edge.source === props.selectedNode.id ? newId : edge.source;
      const newTarget = edge.target === props.selectedNode.id ? newId : edge.target;
      const staticEdge = buildControlFlowEdge({
        sourceNodeId: newSource,
        targetNodeId: newTarget,
      });
      edgeAddChanges.push({
        type: 'add',
        item: staticEdge,
      });
    }

    setIsTabDirty(false);

    let sidepanelChange: SidepanelChange = {
      sidepanel: null,
    };

    if (shouldOpenSidepanel) {
      sidepanelChange = {
        sidepanel: SidepanelType.NODE_SETTINGS,
        nodeId: taskNode.id,
      };
    }
    await sidepanelManager.changeSidepanel({
      sidepanelChange,
      onBeforeSidepanelChanged: async () => {
        await applyFlowchartChanges((flowchartDraftState) => ({
          nodeChanges: [
            {
              type: 'remove',
              id: props.selectedNode.id,
            },
            {
              type: 'add',
              item: taskNode,
            },
          ],
          edgeChanges: [...edgeRemoveChanges, ...edgeAddChanges],
        }));
      },
    });
  };

  if (isPlaceholderForTaskSchemaCreateData(props.selectedNode.data.placeholderForNodeTypeData)) {
    return (
      <CreateProcessTaskDefinitionEditor
        restrictedToExecutionType={props.selectedNode.data.placeholderForNodeTypeData.executionType}
        restrictedToOrganizationId={flowchartTaskSchema.organization.id}
        stickyCreateButton={true}
        onCreateProcessTaskDefinition={async (newTaskDefinition) => {
          assert(
            newTaskDefinition.latestVersion != null,
            'Creating a new task definition is expected to generate a version'
          );
          cacheCreatedTaskDefinition(newTaskDefinition);
          const configurableTaskDefinition = newTaskDefinition as ConfigurableTaskDefinitionGQL;
          await createNodeFromTaskDefinition(configurableTaskDefinition, false);
          analytics.trackEvent(WORKFLOWS_ANALYTICS_ACTIONS.CREATED_NEW_TASK_DEFINITION_FROM_FLOWCHART, {
            actionOrigin: ActionOrigin.WORKFLOWS_FLOWCHART_FIRST_CONFIGURATION_CANVAS,
            nodeType: buildExecutionNodeTypeIdentifier(
              ProcessFlowchartNodeType.TASK_SCHEMA_NODE,
              configurableTaskDefinition.latestVersion.processTaskSchema.executionType
            ),
            taskDefinitionVersionId: configurableTaskDefinition.latestVersion.id,
            taskSchemaId: configurableTaskDefinition.latestVersion.processTaskSchema.id,
            executionType: configurableTaskDefinition.latestVersion.processTaskSchema.executionType,
            flowchartType: flowchartEditorConfigurator.flowchartTypeName,
          });
        }}
      />
    );
  }
  if (isPlaceholderForAddExistingTaskSchemaData(props.selectedNode.data.placeholderForNodeTypeData)) {
    const flowchartOrganization = flowchartTaskSchema.organization;
    const allowedTaskDefinitions = taskDefinitionsForSidepanel.filter(
      (definition) =>
        !isArchived(definition.latestVersion.processTaskSchema) &&
        flowchartOrganization.id === definition.latestVersion.processTaskSchema.organization.id
    );
    return (
      <AddExistingTaskDefinitionPane
        createNodeFromTaskDefinition={createNodeFromTaskDefinition}
        taskDefinitionsForSidepanel={allowedTaskDefinitions}
        flowchartTypeName={flowchartEditorConfigurator.flowchartTypeName}
      />
    );
  } else {
    assert(false, 'Unexpected placeholder type', {
      placeholderForNodeTypeData: props.selectedNode.data.placeholderForNodeTypeData,
    });
    return <></>;
  }
}

const styles = StyleSheet.create({
  wrapper: {
    marginLeft: 8,
  },
  addExistingTaskSchemaOption: {
    paddingTop: 8,
    paddingBottom: 8,
    paddingLeft: 8,
    marginBottom: 8,
    border: `1px solid ${Color.GRAY_200}`,
    ':hover': {
      boxShadow: '0 4px 10px rgba(0,0,0,.08)',
    },
  },
  addExistingTaskSchemaOptionText: {
    marginLeft: 8,
  },
  addExistingTaskSchemaOptionsContainer: {
    marginTop: 8,
  },
});
